import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { publicApi } from "@/lib/api";
import { ArrowLeft, UserPlus, MailCheck } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";

export default function Register() {
  const [sp] = useSearchParams();
  const inviteCode = (sp.get("invite") || "").trim().toUpperCase();
  const [inviteInfo, setInviteInfo] = useState(null); // {valid, segment} | null
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [name, setName] = useState("");
  const [terms, setTerms] = useState(false);
  const [newsletter, setNewsletter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const nav = useNavigate();

  // Validate invite code from URL param (?invite=XYZ) on mount
  useEffect(() => {
    if (!inviteCode) return;
    publicApi.get(`/public/invitations/${inviteCode}`)
      .then(({ data }) => setInviteInfo(data))
      .catch(() => setInviteInfo({ valid: false, reason: "error" }));
  }, [inviteCode]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Hasło musi mieć co najmniej 8 znaków"); return; }
    if (password !== confirmPwd) { toast.error("Hasła nie są identyczne"); return; }
    if (!terms) { toast.error("Musisz zaakceptować regulamin i politykę prywatności"); return; }
    setLoading(true);
    try {
      await publicApi.post("/auth/register", {
        email, password, name,
        terms_accepted: true,
        newsletter_opt_in: newsletter,
        invite_code: inviteInfo?.valid ? inviteCode : null,
      });
      setDone(true);
      toast.success("Konto utworzone. Sprawdź e-mail.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Błąd rejestracji");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-6">
        <div className="max-w-md w-full brut bg-white rounded-md p-8 text-center" data-testid="registration-success">
          <div className="brut bg-[#D1FAE5] w-16 h-16 rounded-md flex items-center justify-center mx-auto mb-4">
            <MailCheck className="w-8 h-8" strokeWidth={2.5} />
          </div>
          <h1 className="font-display font-black text-2xl mb-3">Sprawdź skrzynkę</h1>
          <p className="text-[#334155] mb-2">
            Wysłaliśmy link aktywacyjny na <b>{email}</b>.
          </p>
          <p className="text-sm text-[#64748B] mb-6">
            Kliknij w niego aby zakończyć rejestrację. Link wygasa po 24 godzinach.
            Sprawdź też folder &bdquo;Spam&rdquo;.
          </p>
          <Button
            onClick={async () => {
              try {
                const { data } = await publicApi.post("/auth/resend-verification", { email });
                toast.success(data.message || "Link wysłany ponownie.");
              } catch (e) {
                toast.error(e.response?.data?.detail || "Nie udało się wysłać linku.");
              }
            }}
            variant="outline"
            className="w-full brut brut-hover bg-white hover:bg-[#F1F5F9] border-2 border-[#0F172A] font-bold rounded-md mb-2"
            data-testid="resend-after-register-btn"
          >
            Nie dostałem — wyślij ponownie
          </Button>
          <Button
            onClick={() => nav("/teacher/login")}
            className="w-full brut brut-hover bg-[#0F172A] hover:bg-[#0F172A] text-white font-bold rounded-md"
            data-testid="goto-login-after-register"
          >
            Przejdź do logowania
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      <header className="border-b-2 border-[#0F172A] bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/teacher/login" className="flex items-center gap-2 font-bold">
            <ArrowLeft className="w-4 h-4" strokeWidth={2.5} /><span>Wróć</span>
          </Link>
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#64748B]">Rejestracja nauczyciela</div>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="inline-flex items-center gap-2 brut bg-[#FEF08A] px-3 py-1 rounded-md mb-6">
            <UserPlus className="w-3 h-3" strokeWidth={3} />
            <span className="text-xs tracking-[0.2em] uppercase font-bold">Nowe konto</span>
          </div>
          <h1 className="font-display font-black text-4xl mb-2">Załóż konto</h1>
          <p className="text-[#334155] mb-8">
            Dostęp dla nauczycieli. Tworzysz własne pytania, sesje i klasy.
          </p>

          {inviteCode && inviteInfo?.valid && (
            <div className="brut border-2 border-[#10B981] bg-[#D1FAE5] rounded-md p-3 mb-4 text-sm" data-testid="invite-valid-banner">
              🎟️ Rejestrujesz się z zaproszenia: <b>{inviteInfo.segment}</b>
              {inviteInfo.note && <div className="text-xs text-[#065F46] mt-1 italic">„{inviteInfo.note}&rdquo;</div>}
            </div>
          )}
          {inviteCode && inviteInfo && !inviteInfo.valid && (
            <div className="brut border-2 border-[#F59E0B] bg-[#FEF3C7] rounded-md p-3 mb-4 text-sm" data-testid="invite-invalid-banner">
              ⚠️ Kod zaproszenia <b>{inviteCode}</b> jest{" "}
              {inviteInfo.reason === "exhausted" ? "wyczerpany" : "nieprawidłowy"}
              . Możesz założyć konto bez zaproszenia.
            </div>
          )}

          <form onSubmit={submit} className="brut bg-white rounded-md p-6 space-y-4">
            <div>
              <Label className="font-bold text-sm">Nazwa wyświetlana</Label>
              <Input
                value={name} onChange={(e) => setName(e.target.value)} required maxLength={80}
                className="mt-2 brut-input border-2 border-[#0F172A] rounded-md h-12"
                placeholder="np. Chemik42, Pan Adam, PaniOd Chemii…" data-testid="register-name-input"
              />
              <div className="text-xs text-[#64748B] mt-1.5">
                Nie musi być prawdziwe imię — może być pseudonim. RODO ✅ Pole używane
                w mailach powitalnych i podpisie w panelu.
              </div>
            </div>
            <div>
              <Label className="font-bold text-sm">Adres e-mail</Label>
              <Input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="mt-2 brut-input border-2 border-[#0F172A] rounded-md h-12"
                placeholder="nauczyciel@example.pl" data-testid="register-email-input"
              />
            </div>
            <div>
              <Label className="font-bold text-sm">Hasło (min. 8 znaków)</Label>
              <PasswordInput
                id="register-password"
                value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8}
                className="mt-2 brut-input border-2 border-[#0F172A] rounded-md h-12 font-mono px-3"
                placeholder="••••••••" data-testid="register-password-input"
              />
            </div>
            <div>
              <Label className="font-bold text-sm">Powtórz hasło</Label>
              <PasswordInput
                id="register-confirm-password"
                value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} required minLength={8}
                className={`mt-2 brut-input border-2 rounded-md h-12 font-mono px-3 ${
                  confirmPwd && password !== confirmPwd ? "border-[#EF4444]" : "border-[#0F172A]"
                }`}
                placeholder="••••••••" data-testid="register-confirm-password-input"
              />
              {confirmPwd && password !== confirmPwd && (
                <div className="text-xs text-[#EF4444] mt-1 font-bold" data-testid="password-mismatch-warning">
                  Hasła nie są identyczne
                </div>
              )}
            </div>
            <div className="space-y-2 pt-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <Checkbox
                  checked={terms} onCheckedChange={setTerms}
                  className="mt-0.5 brut border-2 border-[#0F172A] data-[state=checked]:bg-[#0F172A]"
                  data-testid="terms-checkbox"
                />
                <span className="text-sm text-[#334155]">
                  Akceptuję{" "}
                  <Link to="/terms" target="_blank" className="underline font-bold">Regulamin</Link> i{" "}
                  <Link to="/privacy" target="_blank" className="underline font-bold">Politykę prywatności</Link>.
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <Checkbox
                  checked={newsletter} onCheckedChange={setNewsletter}
                  className="mt-0.5 brut border-2 border-[#0F172A] data-[state=checked]:bg-[#0F172A]"
                  data-testid="newsletter-checkbox"
                />
                <span className="text-sm text-[#64748B]">
                  Chcę otrzymywać newsletter o nowych funkcjach i wspólnych pytaniach (opcjonalnie).
                </span>
              </label>
            </div>
            <Button
              type="submit" disabled={loading || !terms}
              className="w-full brut brut-hover bg-[#0F172A] hover:bg-[#0F172A] text-white font-bold rounded-md h-12"
              data-testid="register-submit-btn"
            >
              {loading ? "Tworzę konto..." : "Załóż konto"}
            </Button>
            <div className="text-xs text-center text-[#64748B] pt-2">
              Masz już konto?{" "}
              <Link to="/teacher/login" className="font-bold underline">Zaloguj się</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
